import BaseObject from 'app/src/base/BaseObject';
import Localize from 'app/src/localize/Localize';
import $ from 'jquery';

const _Events = {
  NEED_MANUAL_CREDIT_CARD_UI: 'need:manual:credit:card:ui',
  NO_TERMINAL_CONFIGURED: 'no:terminal:configured',
  PAYMENT_COMPLETE: 'payment:complete',
  SWIPE_DATA_RECEIVED: 'swipe:data:received',
  REFUND_COMPLETE: 'refund:complete',
};

export default class CardReaderDelegate extends window.StencilWebAPI.CardProcessingDelegate {
  static get Event() {
    return _Events;
  }

  constructor() {
    super();
    this._eventGenerator = new BaseObject();
  }

  get eventGenerator() {
    return this._eventGenerator;
  }

  /**
   * Adds a payment terminal transaction to the sale.
   *
   * @param {StencilWebAPI.CardReader.TransactionResultType} result   The result of the transaction
   * @param {string} payment.saleID   The ID of the sale for the transaction
   * @param {Object} payment     An object hash containing information about the payment:
   * @param {string} payment.ccChargeID   The CCCharge ID (if any) for the transaction; likely to be deprecated soon
   * @param {string} payment.paymentTypeID    The ID of the type of payment for the transaction
   * @param {string} payment.salePaymentAmount    The amount of the payment
   *
   */
  paymentTerminalTransactionResult(result, payment) {
    switch (result) {
      case window.StencilWebAPI.CardReader.TransactionResultType.SUCCESSFUL_PAYMENT:
        if (payment) {
          this.eventGenerator.trigger(_Events.PAYMENT_COMPLETE, payment);
        } else {
          console.error('Invalid payment data:', payment);
        }
        break;

      case window.StencilWebAPI.CardReader.TransactionResultType.NEED_MANUAL_CREDIT_CARD_UI:
        this.eventGenerator.trigger(_Events.NEED_MANUAL_CREDIT_CARD_UI);
        break;

      case window.StencilWebAPI.CardReader.TransactionResultType.NO_PAYMENT_TERMINAL_CONFIGURED:
        this.eventGenerator.trigger(_Events.NO_TERMINAL_CONFIGURED);
        break;

      default:
        console.error('Invalid transaction type:', result);
        break;
    }
  }

  /**
   * Removes a terminal transaction from the sale.  Used for transactions with a transaction
   * reference ID and no server-side support for voiding terminal transactions.
   *
   * @param {StencilWebAPI.CardReader.TransactionRefundByRefError} result     The result of the refund by reference
   * @param {string} transactionReferenceID  The transaction reference ID
   * @param {string} salePaymentID    The ID of the payment that was refunded
   * @param {StencilWebAPI.CardReader.TransactionRefundByRefError} errorReason    The reason for the error
   * @param {string} errorMessage    The error message to display when an error occurs during a refund
   */
  paymentTerminalRefundResult(
    result,
    transactionReferenceID,
    salePaymentID,
    errorReason,
    errorMessage
  ) {
    switch (result) {
      case window.StencilWebAPI.CardReader.TransactionRefundByRefType.REFUND_SUCCESSFUL:
        if (transactionReferenceID) {
          this.eventGenerator.trigger(_Events.REFUND_COMPLETE, {
            transactionReferenceID,
            salePaymentID,
          });
        } else {
          console.error('Invalid refund result.  No reference ID specified.');
        }
        break;

      case window.StencilWebAPI.CardReader.TransactionRefundByRefType.REFUND_NOT_COMPLETED:
        if (
          errorReason === window.StencilWebAPI.CardReader.TransactionRefundByRefError.REFUND_FAILED
        ) {
          alertUser(Localize.translate(errorMessage || 'An unknown error has occurred.'));
        } else if (
          errorReason ===
          window.StencilWebAPI.CardReader.TransactionRefundByRefError
            .REFUND_FAILED_MISSING_OR_MALFORMED_DATA
        ) {
          console.error('Invalid data supplied to the the native application.');
        }
        break;

      case window.StencilWebAPI.CardReader.TransactionRefundByRefType
        .NO_PAYMENT_TERMINAL_CONFIGURED:
        alertUser(
          Localize.translate(
            'Voiding terminal payments requires that a terminal is configured in the application.'
          )
        );
        break;

      default:
        console.error('Invalid refund transaction response:', result);
        break;
    }
  }

  /*
   * TODO
   * We really need to refactor the StencilWebAPI since rather than just giving us the data
   * we need and letting us handle it, it's trying to perform view logic for us.
   */

  /**
   * The delegate operation which is invoked when we receive swipe data from Stencil.  The
   * Stencil Web API is currently structured so that it tries to control the UI for us so
   * we need to return an input element.  We create a dummy element for the Stencil Web API
   * to write to which we then use in submitSwipe() to retrieve the value dropped into it
   * by the Stencil Web API.
   *
   * @returns {jQuery|HTMLElement|*}
   */
  getCardInput() {
    // create a dummy container that we can use later to submit the swipe
    this._$cardInput = $('<input/>');

    return this._$cardInput;
  }

  /**
   * Invoked by the Stencil Web API to submit the swipe.  We read the data written to
   * the element by Stencil Web API and parse the result into a card number which we
   * send along with the notification.
   */
  submitSwipe() {
    if (this._$cardInput) {
      this.eventGenerator.trigger(_Events.SWIPE_DATA_RECEIVED, this._$cardInput.val());
    }
  }
}
